"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { SitePlanEditor, SitePlanPrint, useSitePlan } from "@/components/tools/SitePlanCanvas";
import {
  Field,
  TextInput,
  TextArea,
  CheckboxRow,
  GuardedPrintButton,
  CompletionPanel,
  TableScroll,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* Construction site fire plan builder — the fire safety arrangements for a
   construction site (NOT a premises fire risk assessment under the RRFSO).
   Grounded in HSE HSG168 "Fire safety in construction" and the RamsDocs
   knowledge bank. The plan documents arrangements; adequacy is a competent-
   person judgement and a complex/occupied building needs a proper FRA.

   Each ignition source the user ticks contributes its own tailored controls to
   the printed plan, and (where the source implies a particular extinguishing
   medium) drives the fire-fighting equipment suggestions. The post-hot-work
   fire-watch minimum is one hour (60 minutes), with a further check two hours
   after work stops — HSG168, "Fire safety in construction". */

interface IgnitionSource {
  id: string;
  label: string;
  /** Tailored controls added to the plan when this source is present. */
  controls: string[];
  /** Extinguisher / fire-point note this source contributes (deduped). */
  firefighting?: string;
}

const IGNITION_SOURCES: IgnitionSource[] = [
  {
    id: "hot-works",
    label: "Hot works (welding, cutting, grinding, soldering, torch-on roofing)",
    controls: [
      "Hot works controlled by a permit-to-work system (a hot-works permit tool is coming soon); one permit per task and location, valid for a single work period only.",
      "Combustibles removed or protected within the work area before any flame; the area kept damp where practicable.",
      "A dedicated fire watcher with no other duties present throughout the work and for at least 60 minutes after the last flame, with a further check two hours after work stops.",
      "All hot works finished at least two hours before the end of the working day so the fire checks can be completed before the site is left.",
      "Suitable extinguishers (CO2, water) and a fire blanket immediately to hand at the point of work; gas cylinders fitted with flashback arrestors and stored/transported safely.",
    ],
    firefighting: "CO2 and water extinguisher plus a fire blanket at every hot-works location.",
  },
  {
    id: "temp-electrics",
    label: "Temporary electrics & battery / Li-ion charging stations",
    controls: [
      "Temporary installation inspected and tested by a competent person; 110V reduced-voltage equipment used where reasonably practicable, no daisy-chained leads or overloaded sockets.",
      "Designated charging station for battery tools and Li-ion packs, away from escape routes and combustible storage, on a non-combustible surface and not left charging unattended overnight.",
      "Damaged, swollen or overheating batteries quarantined in a metal container away from the building; only manufacturer-approved chargers used.",
    ],
    firefighting: "CO2 extinguisher at distribution boards and charging stations (electrical risk).",
  },
  {
    id: "heaters",
    label: "Temporary heaters / drying equipment",
    controls: [
      "Heaters fixed in position, guarded, and kept well clear of combustible materials, sheeting and drying timber; LPG / fuel-burning heaters used only with adequate ventilation.",
      "Heaters switched off and made safe when the area is unattended; not used to dry clothing or materials draped over them.",
    ],
  },
  {
    id: "smoking",
    label: "Smoking",
    controls: [
      "Smoking and vaping prohibited inside buildings and near flammable storage; permitted only in a designated external area with metal receptacles for discarding materials.",
      "No-smoking rules covered at induction and signed at entrances and flammable storage areas.",
    ],
  },
  {
    id: "bitumen",
    label: "Bitumen boilers / hot bitumen",
    controls: [
      "Bitumen boilers sited on a level, non-combustible base in the open air, away from the building and escape routes, never on a roof or inside a structure.",
      "Boiler attended at all times while alight, with the correct fire-fighting media (dry powder / fire blanket — never water) kept beside it; LPG supply isolated when not in use.",
    ],
    firefighting: "Dry-powder extinguisher and fire blanket beside any bitumen boiler — do not use water on a bitumen fire.",
  },
  {
    id: "refuelling",
    label: "Plant / generator refuelling & fuel storage",
    controls: [
      "Refuelling carried out with the engine off in a designated area away from ignition sources and drains; spill kit available and no smoking enforced.",
      "Fuel stored in a bunded, lockable store away from the building; quantities on site kept to the working minimum.",
    ],
    firefighting: "Dry-powder / foam extinguisher at the refuelling and fuel-storage area.",
  },
];

interface SectionDef {
  id: string;
  prompts: string[];
}

/* Standing sections every construction fire plan should cover (HSG168). Each is
   a checklist of arrangements; only ticked items appear in the printed plan. */
const COMBUSTIBLES: SectionDef = {
  id: "combustibles",
  prompts: [
    "Flammable liquids and gases kept to the minimum needed; LPG and acetylene cylinders stored in a ventilated external cage, segregated (oxygen apart from fuel gases) and a safe distance from the building and excavations.",
    "Waste cleared regularly and not allowed to accumulate; skips sited a safe distance from buildings and the boundary, and not overfilled or set alight.",
    "Timber, packaging and combustible insulation kept controlled, covered and away from ignition sources; deliveries broken down rather than stockpiled.",
  ],
};

const DETECTION: SectionDef = {
  id: "detection",
  prompts: [
    "An audible means of raising the alarm provided across the whole site (e.g. klaxon, air horn, or temporary wireless detection units) and audible above site noise.",
    "Anyone discovering a fire raises the alarm immediately and the site is evacuated; the person in charge / fire coordinator confirms the alarm has been raised and calls the fire service.",
    "The alarm signal is briefed at induction so everyone recognises it; temporary detection extended as the building is closed in and existing systems are reinstated.",
  ],
};

const ESCAPE: SectionDef = {
  id: "escape",
  prompts: [
    "Escape routes kept clear, unobstructed and signed; travel distances and the number of routes reviewed and increased as the building stage changes.",
    "Temporary escape lighting provided where natural light is inadequate (e.g. internal works, basements, enclosed stairs).",
    "A defined assembly point clear of the building and access routes, briefed at induction; no one re-enters until the fire service confirms it is safe.",
  ],
};

const EMERGENCY: SectionDef = {
  id: "emergency",
  prompts: [
    "Call 999 — site address, postcode and what3words / nearest junction displayed by every site entrance and telephone point.",
    "Site access kept clear for fire appliances; hardstanding, gates and any keys/codes arranged so appliances can reach the building.",
    "Gas and electrical isolation points identified and labelled so supplies can be cut off in an emergency.",
  ],
};

const COORDINATION: SectionDef = {
  id: "coordination",
  prompts: [
    "The fire plan shared with all contractors on site and reviewed as the works and building stage change.",
    "Fire arrangements (alarm, escape routes, assembly point, hot-works rules) covered in the site induction for everyone before they start.",
    "Hot-works hours controlled and fire-watch responsibilities assigned, with completed permits reviewed by senior site staff.",
  ],
};

const STANDING_SECTIONS: { def: SectionDef; title: string; blurb: string }[] = [
  { def: COMBUSTIBLES, title: "Combustibles & fuel storage", blurb: "Keeping the fuel for a fire off the site and away from the building." },
  { def: DETECTION, title: "Detection & warning", blurb: "How a fire is detected and how everyone is alerted." },
  { def: ESCAPE, title: "Means of escape", blurb: "Getting everyone out — routes, lighting and where they gather." },
  { def: EMERGENCY, title: "Emergency procedures", blurb: "Calling the fire service and cutting off the supplies." },
  { def: COORDINATION, title: "Coordination & inductions", blurb: "Sharing the plan and controlling high-risk work." },
];

const BASELINE_FIREFIGHTING = [
  "Fire points with water / foam extinguishers for general (Class A) combustibles, located on escape routes and at high-risk areas, signed and unobstructed.",
  "Fire-fighting equipment checked weekly (and after use), with a record kept; everyone shown the nearest fire point at induction.",
];

interface ExtinguisherRow {
  id: number;
  type: string;
  location: string;
  inspected: string;
}

let nextExtId = 1;
const emptyExtinguisher = (): ExtinguisherRow => ({ id: nextExtId++, type: "", location: "", inspected: "" });

export function FirePlanBuilder() {
  // Header
  const [site, setSite] = useState("");
  const [contractor, setContractor] = useState("");
  const [coordinator, setCoordinator] = useState("");

  // Ignition sources present on site
  const [sources, setSources] = useState<Record<string, boolean>>({});

  // Standing-section checklists (id -> set of ticked prompt indices)
  const [checks, setChecks] = useState<Record<string, boolean>>({});

  // Hot works branch.
  const [hotWorks, setHotWorks] = useState<"" | "yes" | "no">("");

  // Extinguisher schedule.
  const [extinguishers, setExtinguishers] = useState<ExtinguisherRow[]>([emptyExtinguisher()]);

  // Free-text additions
  const [notes, setNotes] = useState("");

  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const [sitePlan, setSitePlan] = useSitePlan("fire-plan");

  const resultTracked = useRef(false);

  const updateExtinguisher = (id: number, patch: Partial<ExtinguisherRow>) =>
    setExtinguishers((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const filledExtinguishers = useMemo(
    () => extinguishers.filter((e) => e.type.trim() || e.location.trim() || e.inspected.trim()),
    [extinguishers],
  );

  const toggleSource = (id: string, v: boolean) => setSources((s) => ({ ...s, [id]: v }));
  const toggleCheck = (key: string, v: boolean) => setChecks((c) => ({ ...c, [key]: v }));

  const activeSources = useMemo(
    () => IGNITION_SOURCES.filter((s) => sources[s.id]),
    [sources],
  );

  // Fire-fighting list = baseline + a deduped set of source-specific notes.
  const firefighting = useMemo(() => {
    const extra = activeSources.map((s) => s.firefighting).filter((x): x is string => !!x);
    return [...BASELINE_FIREFIGHTING, ...Array.from(new Set(extra))];
  }, [activeSources]);

  // Which standing sections have at least one ticked prompt (printed only then).
  const completedStanding = useMemo(
    () =>
      STANDING_SECTIONS.map((s) => ({
        ...s,
        ticked: s.def.prompts
          .map((p, i) => ({ p, on: !!checks[`${s.def.id}:${i}`] }))
          .filter((x) => x.on)
          .map((x) => x.p),
      })).filter((s) => s.ticked.length > 0),
    [checks],
  );

  // The plan is "started" once there's anything worth printing.
  const hasContent =
    !!site ||
    !!contractor ||
    !!coordinator ||
    activeSources.length > 0 ||
    completedStanding.length > 0 ||
    hotWorks !== "" ||
    filledExtinguishers.length > 0 ||
    !!notes.trim();

  // Core a construction fire plan shouldn't ship without: who/where, the named
  // fire coordinator, the standing arrangements worked through, the hot-works
  // decision and at least one fire-fighting point on the schedule.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Site / project", done: site.trim().length > 0 },
    { label: "Principal / main contractor", done: contractor.trim().length > 0 },
    { label: "Fire marshal / coordinator", done: coordinator.trim().length > 0 },
    { label: "Detection & warning worked through", done: completedStanding.some((s) => s.def.id === "detection") },
    { label: "Means of escape worked through", done: completedStanding.some((s) => s.def.id === "escape") },
    { label: "Emergency procedures worked through", done: completedStanding.some((s) => s.def.id === "emergency") },
    { label: "Hot works question answered", done: hotWorks !== "" },
    { label: "At least one extinguisher on the schedule", done: filledExtinguishers.length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  // Fire the funnel event once per visit when the plan first has real content.
  useEffect(() => {
    if (hasContent && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("fire-plan", { ignitionSources: activeSources.length });
    }
  }, [hasContent, activeSources.length]);

  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
      {/* ── Inputs ─────────────────────────────────────────────── */}
      <div className="space-y-6">
        {/* Site & responsible person */}
        <div className="card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 1</div>
          <h2 className="mt-1 text-xl">Site & responsible person</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="Site / project" className="sm:col-span-2">
              <TextInput value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. 14 Mill Lane refurbishment" />
            </Field>
            <Field label="Principal / main contractor">
              <TextInput value={contractor} onChange={(e) => setContractor(e.target.value)} placeholder="Company name" />
            </Field>
            <Field label="Fire marshal / coordinator" hint="The named person responsible for fire safety on site.">
              <TextInput value={coordinator} onChange={(e) => setCoordinator(e.target.value)} placeholder="Name" />
            </Field>
          </div>
          <p className="mt-3 text-[0.78rem] text-muted">Date: {today} (set automatically for the printed plan).</p>
        </div>

        {/* Ignition sources */}
        <div className="card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 2</div>
          <h2 className="mt-1 text-xl">Ignition sources present</h2>
          <p className="mt-1 text-sm text-muted">
            Tick the sources of ignition on this site. Each one adds the controls it needs to your plan, and matches
            the right fire-fighting equipment to the risk.
          </p>
          <div className="mt-4 space-y-2">
            {IGNITION_SOURCES.map((s) => (
              <div key={s.id}>
                <CheckboxRow checked={!!sources[s.id]} onChange={(v) => toggleSource(s.id, v)}>
                  {s.label}
                </CheckboxRow>
                {sources[s.id] && (
                  <ul className="mb-1 mt-1.5 ml-7 list-disc space-y-1 text-[0.84rem] leading-snug text-muted">
                    {s.controls.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          {activeSources.length === 0 && (
            <p className="mt-3 text-[0.82rem] text-muted">
              Even with no high-risk source ticked, the standing sections below still apply to every construction site.
            </p>
          )}
        </div>

        {/* Hot works branch */}
        <div className="card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Step 2a</div>
          <h2 className="mt-1 text-xl">Hot works</h2>
          <p className="mt-1 text-sm text-muted">
            Welding, cutting, grinding, soldering or torch-on work. If hot works will take place, they must be
            controlled by a hot-works permit.
          </p>
          <div className="mt-4 flex gap-2">
            {(["yes", "no"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setHotWorks(opt)}
                className={`rounded-full px-4 py-1.5 text-[0.85rem] font-semibold capitalize transition-colors ${
                  hotWorks === opt
                    ? "bg-ink text-white"
                    : "border border-line-2 text-muted hover:border-ink hover:text-ink"
                }`}
              >
                {opt === "yes" ? "Yes — hot works will take place" : "No hot works"}
              </button>
            ))}
          </div>
          {hotWorks === "yes" && (
            <div className="mt-4 rounded-lg border border-risk-high/30 bg-[#fef0e6] px-4 py-3 text-[0.85rem] leading-relaxed text-[#b34e10]">
              Issue a hot-works permit for each task: clear and protect combustibles, keep extinguishers and a fire
              blanket at the point of work, and post a fire watch for at least 60 minutes after the last flame.{" "}
              <a href="/tools/hot-works-permit/" className="font-semibold underline">
                Create a hot-works permit
              </a>{" "}
              for each task and keep it with this plan.
            </div>
          )}
        </div>

        {/* Standing sections */}
        {STANDING_SECTIONS.map((s, idx) => (
          <div key={s.def.id} className="card shadow-soft p-6">
            <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
              Step {3 + idx}
            </div>
            <h2 className="mt-1 text-xl">{s.title}</h2>
            <p className="mt-1 text-sm text-muted">{s.blurb}</p>
            <div className="mt-4 space-y-2">
              {s.def.prompts.map((p, i) => {
                const key = `${s.def.id}:${i}`;
                return (
                  <CheckboxRow key={key} checked={!!checks[key]} onChange={(v) => toggleCheck(key, v)}>
                    {p}
                  </CheckboxRow>
                );
              })}
            </div>
          </div>
        ))}

        {/* Fire-fighting equipment summary (driven by ticked risks) */}
        <div className="card shadow-soft p-6">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
            Step {3 + STANDING_SECTIONS.length}
          </div>
          <h2 className="mt-1 text-xl">Fire-fighting equipment</h2>
          <p className="mt-1 text-sm text-muted">
            These extinguisher types are matched to the risks you ticked above. Record the actual locations on site in
            the notes box below.
          </p>
          <ul className="mt-4 list-disc space-y-1.5 pl-5 text-[0.9rem] leading-snug text-body">
            {firefighting.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>

          {/* Extinguisher schedule */}
          <div className="mt-5">
            <span className="block text-[0.85rem] font-semibold text-ink">Extinguisher schedule</span>
            <span className="mt-0.5 block text-[0.78rem] text-muted">
              The extinguishers actually on site: type, where each is sited, and when it was last inspected.
            </span>
            <div className="mt-3 space-y-3">
              {extinguishers.map((e, i) => (
                <div key={e.id} className="rounded-xl border border-line bg-mist/60 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Extinguisher {i + 1}</span>
                    {extinguishers.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setExtinguishers((rs) => rs.filter((x) => x.id !== e.id))}
                        className="text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <Field label="Type">
                      <TextInput value={e.type} onChange={(ev) => updateExtinguisher(e.id, { type: ev.target.value })} placeholder="e.g. CO2 / water / dry powder" />
                    </Field>
                    <Field label="Location">
                      <TextInput value={e.location} onChange={(ev) => updateExtinguisher(e.id, { location: ev.target.value })} placeholder="e.g. Scaffold base, east" />
                    </Field>
                    <Field label="Last inspected">
                      <TextInput type="date" value={e.inspected} onChange={(ev) => updateExtinguisher(e.id, { inspected: ev.target.value })} />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setExtinguishers((rs) => [...rs, emptyExtinguisher()])}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
            >
              + Add extinguisher
            </button>
          </div>

          <Field label="Fire point locations & site-specific notes" className="mt-4">
            <TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Fire points at site entrance, scaffold base and first-floor landing. LPG cage 6m from the building, north corner. Isolation: gas at meter cupboard, mains at site cabin."
            />
          </Field>
        </div>

        <SitePlanEditor
          value={sitePlan}
          onChange={setSitePlan}
          defaultPreset="fire"
          title="Fire plan sketch"
          hint="Mark the escape routes, fire points, assembly point and any buildings or exclusion areas. The sketch prints with the fire plan."
        />

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* ── Printable plan ─────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Plan completion" />
        </div>
        {hasContent ? (
          <>
            <div className="print-area card shadow-soft p-6" id="fire-plan-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Construction site fire plan
              </div>
              <h2 className="mt-1 text-lg">{site || "Untitled site"}</h2>
              <div className="mt-1 text-[0.82rem] text-muted">
                {[
                  contractor && `Contractor: ${contractor}`,
                  coordinator && `Fire coordinator: ${coordinator}`,
                  today,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>

              {/* Ignition sources & their controls */}
              {activeSources.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                    Ignition sources & controls
                  </h3>
                  {activeSources.map((s) => (
                    <div key={s.id} className="mt-2">
                      <div className="text-[0.9rem] font-semibold text-ink">{s.label}</div>
                      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[0.86rem] leading-snug text-body">
                        {s.controls.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {/* Standing sections — only those with ticked items */}
              {completedStanding.map((s) => (
                <div key={s.def.id} className="mt-5">
                  <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">{s.title}</h3>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[0.86rem] leading-snug text-body">
                    {s.ticked.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Hot works */}
              {hotWorks !== "" && (
                <div className="mt-5">
                  <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">Hot works</h3>
                  <p className="mt-1 text-[0.86rem] leading-snug text-body">
                    {hotWorks === "yes"
                      ? "Hot works will take place and are controlled by a hot-works permit issued for each task, with combustibles cleared/protected, extinguishers and a fire blanket at the point of work, and a fire watch maintained for at least 60 minutes after the last flame."
                      : "No hot works are planned. Any hot work introduced later requires a hot-works permit before it starts."}
                  </p>
                </div>
              )}

              {/* Fire-fighting equipment */}
              <div className="mt-5">
                <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-muted">
                  Fire-fighting equipment
                </h3>
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[0.86rem] leading-snug text-body">
                  {firefighting.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                {filledExtinguishers.length > 0 && (
                  <TableScroll>
                    <table className="mt-3 w-full min-w-[420px] border-collapse text-[0.82rem]">
                      <thead>
                        <tr className="text-left text-[0.72rem] uppercase tracking-wide text-muted">
                          <th className="border border-line px-2 py-1.5 font-semibold">Type</th>
                          <th className="border border-line px-2 py-1.5 font-semibold">Location</th>
                          <th className="border border-line px-2 py-1.5 font-semibold">Last inspected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filledExtinguishers.map((e) => (
                          <tr key={e.id}>
                            <td className="border border-line px-2 py-2 text-ink">{e.type.trim() || "—"}</td>
                            <td className="border border-line px-2 py-2 text-body">{e.location.trim() || "—"}</td>
                            <td className="border border-line px-2 py-2 text-body">{e.inspected.trim() || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                )}
                {notes.trim() && (
                  <p className="mt-1.5 text-[0.86rem] leading-snug text-body">
                    <strong>Locations & notes:</strong> {notes}
                  </p>
                )}
              </div>

              {/* Sign-off */}
              <div className="mt-6 border-t border-line pt-3 text-[0.82rem] text-body">
                <p className="text-[0.78rem] text-muted">
                  This plan sets out the fire safety arrangements for the construction phase. It is not a fire risk
                  assessment for an occupied or completed building. The adequacy of these arrangements is a
                  competent-person judgement and must be reviewed as the works progress.
                </p>
              </div>

              <SitePlanPrint value={sitePlan} heading="Fire plan sketch — escape routes & fire points" />

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="site fire plan builder" />
            </div>
            <GuardedPrintButton
              tool="fire-plan"
              label="Print / save plan as PDF"
              missing={missingRequired}
              warningTitle="This fire plan still has empty core sections"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Fill in the site details, tick the ignition sources present and work through the sections to build a
            printable construction site fire plan. Only the sections you complete appear in the plan.
          </div>
        )}
      </div>
    </div>
  );
}
